import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Shield, HelpCircle,
  MessageSquare, ChevronRight, ChevronLeft,
  Navigation, Languages, Trash2, Check,
  Send, ExternalLink, Loader2, Code2,
  Palette, Map as MapIcon, Moon, Sun, Monitor, Satellite, TreePine, Navigation2, Box, Settings2,
  Smartphone, ShieldCheck, ShieldOff, RotateCcw, MapPinned, AlertTriangle, Mail, CloudSun,
  Bug, Lightbulb, MessageCircle, Car, Bike, Bus, PersonStanding, Route,
} from 'lucide-react';
import { SNAP_BAND_LIMITS } from '../../features/geometry-editing/roadSnapBands';
import { useState, useCallback, useEffect } from 'react';
import { useSettingsStore, type Settings } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { useTourStore } from '../../stores/tourStore';
import { enable2FA, verify2FASetup, disable2FA } from '../../services/api';
import { appearanceTileAccent } from '../ui/DynamicIcon';
import type { LucideIcon } from 'lucide-react';
import { MARKETING_URL, CONSOLE_URL } from '../../lib/env';
import { AppBrandFooter } from '../common/AppBrandFooter';
import { InstallAppSection } from '../pwa/InstallAppSection';

type MapStyle = 'standard' | 'dark' | 'satellite' | 'outdoors' | 'navigation';

type AppearanceAccentKey = keyof typeof appearanceTileAccent;

export type SettingsPanelSection =
  | 'main'
  | 'appearance'
  | 'navigation'
  | 'field_mapping'
  | 'privacy'
  | 'security'
  | 'language'
  | 'help'
  | 'feedback';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** When the panel opens, jump to this section (e.g. deep link or FAB shortcut). */
  initialSection?: SettingsPanelSection;
  currentMapStyle?: MapStyle;
  onMapStyleChange?: (style: MapStyle) => void;
  onOpenLayerSettings?: () => void;
  onOpenWeatherForecast?: () => void;
  onToggle3D?: (enabled: boolean) => void;
  is3DEnabled?: boolean;
  /** Main map only: jump to “My field campaigns” at the bottom of the screen. */
  onBrowseFieldCampaigns?: () => void;
  fieldCampaignsDisabled?: boolean;
  fieldCampaignsSubtitle?: string;
  onOpenFindPostcode?: () => void;
}

function ClearButton({ label, onClear }: { label: string; onClear: () => void }) {
  const [confirmed, setConfirmed] = useState(false);

  const handleClick = useCallback(() => {
    if (confirmed) return;
    try { onClear(); } catch { /* ignore */ }
    setConfirmed(true);
    setTimeout(() => setConfirmed(false), 2000);
  }, [confirmed, onClear]);

  return (
    <button
      onClick={handleClick}
      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all ${confirmed
        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800'
        : 'bg-gray-50 dark:bg-gray-800 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-gray-200 dark:border-gray-700'
        }`}
    >
      {confirmed ? (
        <><Check size={13} /> Cleared</>
      ) : (
        <><Trash2 size={13} /> <span className="whitespace-nowrap">Clear {label}</span></>
      )}
    </button>
  );
}

const FEEDBACK_API = `${MARKETING_URL}/api/contact`;

function FeedbackForm() {
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | 'general'>('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(FEEDBACK_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: email || 'Anonymous Map User',
          email: email || 'noreply@maps.coordal.com',
          message: `[${feedbackType.toUpperCase()}] ${message}`,
          type: 'feedback',
          source: 'coordal-maps-app',
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('sent');
      setMessage('');
    } catch {
      setStatus('error');
    }
  }, [message, email, feedbackType]);

  if (status === 'sent') {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-3">
          <Check size={24} className="text-green-600 dark:text-green-400" />
        </div>
        <p className="font-medium text-gray-900 dark:text-white">Thank you!</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Your feedback helps us improve Coordal Maps.</p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          Send more feedback
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Type</h4>
        <div className="grid grid-cols-3 gap-2">
          {([
            { value: 'general', label: 'General', Icon: MessageCircle },
            { value: 'bug', label: 'Bug Report', Icon: Bug },
            { value: 'feature', label: 'Feature', Icon: Lightbulb },
          ] as const).map((t) => (
            <button
              key={t.value}
              onClick={() => setFeedbackType(t.value)}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border-2 transition-colors ${feedbackType === t.value
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
            >
              <t.Icon size={14} aria-hidden />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-500 dark:text-gray-400 block mb-1.5">Message *</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          placeholder={
            feedbackType === 'bug'
              ? 'Describe the issue — what happened vs what you expected...'
              : feedbackType === 'feature'
                ? 'Describe the feature you would like to see...'
                : 'Share your thoughts about Coordal Maps...'
          }
          className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary-500 resize-none"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-500 dark:text-gray-400 block mb-1.5">Email (optional)</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com — for follow-up"
          className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary-500"
        />
      </div>

      {status === 'error' && (
        <p className="text-xs text-red-500 dark:text-red-400">Failed to send. Please try again or email support@coordal.com.</p>
      )}

      <button
        onClick={handleSubmit}
        disabled={!message.trim() || status === 'sending'}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-xl text-sm font-medium transition-colors"
      >
        {status === 'sending' ? (
          <><Loader2 size={16} className="animate-spin" /> Sending...</>
        ) : (
          <><Send size={16} /> Send Feedback</>
        )}
      </button>
    </div>
  );
}

function SecuritySection() {
  const { user, isAuthenticated } = useAuthStore();
  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.two_factor_enabled || false);
  const [qrUrl, setQrUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await enable2FA();
      setQrUrl(data.qr_code_url);
      setSecret(data.secret);
      setBackupCodes(data.backup_codes);
    } catch (err: any) {
      setError(err.message || 'Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await verify2FASetup(verifyCode);
      setIs2FAEnabled(true);
      setQrUrl('');
      setSecret('');
      setBackupCodes([]);
      setVerifyCode('');
      setSuccess('Two-factor authentication has been enabled.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword || disableCode.length < 6) return;
    setLoading(true);
    setError('');
    try {
      await disable2FA(disablePassword, disableCode);
      setIs2FAEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
      setDisableCode('');
      setSuccess('Two-factor authentication has been disabled.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-8">
        <Shield size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Sign in to manage security settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${is2FAEnabled
        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${is2FAEnabled
          ? 'bg-emerald-100 dark:bg-emerald-900/40'
          : 'bg-gray-200 dark:bg-gray-700'
          }`}>
          {is2FAEnabled
            ? <ShieldCheck size={20} className="text-emerald-600 dark:text-emerald-400" />
            : <ShieldOff size={20} className="text-gray-400 dark:text-gray-500" />
          }
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-white">
            {is2FAEnabled ? 'Two-factor authentication is on' : 'Two-factor authentication is off'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {is2FAEnabled
              ? 'Your account is protected with an authenticator app.'
              : 'Add an extra layer of security to your account.'}
          </p>
        </div>
      </div>

      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Enable flow */}
      {!is2FAEnabled && !qrUrl && (
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Smartphone size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">How it works</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Use an authenticator app like Google Authenticator or Authy to generate a verification code each time you sign in.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleEnable2FA}
            disabled={loading}
            className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Setting up...</> : 'Enable 2FA'}
          </button>
        </div>
      )}

      {/* QR Code + verify */}
      {!is2FAEnabled && qrUrl && (
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 text-center">Scan this QR code with your authenticator app:</p>
            <div className="flex justify-center mb-3">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrUrl)}`}
                alt="2FA QR Code"
                className="w-44 h-44 rounded-lg"
              />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              Or enter manually: <code className="text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-[10px]">{secret}</code>
            </p>
          </div>

          {backupCodes.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">Save your backup codes (shown only once):</p>
              <div className="grid grid-cols-2 gap-1">
                {backupCodes.map((code, i) => (
                  <span key={i} className="font-mono text-xs text-gray-600 dark:text-gray-300">{code}</span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Enter verification code</label>
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white text-center tracking-[0.3em] font-mono placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors"
            />
          </div>

          <button
            onClick={handleVerify}
            disabled={loading || verifyCode.length !== 6}
            className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Verifying...</> : 'Verify & Enable'}
          </button>
        </div>
      )}

      {/* Disable flow */}
      {is2FAEnabled && !showDisable && (
        <button
          onClick={() => { setShowDisable(true); setError(''); }}
          className="w-full px-4 py-2.5 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg text-sm font-medium transition-colors"
        >
          Disable 2FA
        </button>
      )}

      {is2FAEnabled && showDisable && (
        <div className="space-y-4 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl">
          <p className="text-sm text-red-700 dark:text-red-400 font-medium">Confirm disable 2FA</p>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Password</label>
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-primary-500 focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Authenticator code</label>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white text-center tracking-[0.3em] font-mono placeholder-gray-400 focus:border-primary-500 focus:outline-none transition-colors"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setShowDisable(false); setDisablePassword(''); setDisableCode(''); setError(''); }}
              className="flex-1 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDisable}
              disabled={loading || !disablePassword || disableCode.length < 6}
              className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Disabling...</> : 'Disable'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SettingsPanel({
  isOpen,
  onClose,
  initialSection,
  currentMapStyle,
  onMapStyleChange,
  onOpenLayerSettings,
  onOpenWeatherForecast,
  onToggle3D,
  is3DEnabled = false,
  onBrowseFieldCampaigns,
  fieldCampaignsDisabled = false,
  fieldCampaignsSubtitle,
  onOpenFindPostcode,
}: SettingsPanelProps) {
  const [currentSection, setCurrentSection] = useState<SettingsPanelSection>('main');
  const [searchHistoryOptOutOpen, setSearchHistoryOptOutOpen] = useState(false);
  const { settings, updateSetting, clearSearchHistory, clearLocationHistory } = useSettingsStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isOpen) {
      setCurrentSection(initialSection ?? 'main');
    } else {
      setCurrentSection('main');
    }
  }, [isOpen, initialSection]);

  const handleUpdateSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    updateSetting(key, value);
  };

  const handleSearchHistoryToggle = (enabled: boolean) => {
    if (!enabled && settings.searchHistory) {
      setSearchHistoryOptOutOpen(true);
      return;
    }
    handleUpdateSetting('searchHistory', enabled);
  };

  const confirmSearchHistoryDeviceOnly = () => {
    handleUpdateSetting('searchHistory', false);
    setSearchHistoryOptOutOpen(false);
  };

  if (!isOpen) return null;

  const renderToggle = (value: boolean, onChange: (v: boolean) => void) => (
    <button
      onClick={() => onChange(!value)}
      className={`relative w-12 h-7 rounded-full transition-colors ${value ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
    >
      <div
        className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'
          }`}
      />
    </button>
  );

  const renderMainMenu = () => (
    <div className="space-y-1">
      {onOpenFindPostcode && (
        <button
          type="button"
          onClick={onOpenFindPostcode}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
            <Mail size={20} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-gray-900 dark:text-white">Find My Postcode</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Pin drop or current location</p>
          </div>
          <ChevronRight size={20} className="text-gray-400" />
        </button>
      )}

      <button
        onClick={() => setCurrentSection('appearance')}
        className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
          <Palette size={20} className="text-purple-600 dark:text-purple-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">Appearance</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Map style, theme</p>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </button>

      {onOpenWeatherForecast && (
        <button
          type="button"
          onClick={onOpenWeatherForecast}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="w-10 h-10 bg-sky-100 dark:bg-sky-900/30 rounded-full flex items-center justify-center">
            <CloudSun size={20} className="text-sky-600 dark:text-sky-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-gray-900 dark:text-white">Weather Forecast</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">7-day map overlay and dashboard</p>
          </div>
          <ChevronRight size={20} className="text-gray-400" />
        </button>
      )}

      {isAuthenticated && onBrowseFieldCampaigns && !fieldCampaignsDisabled && (
        <button
          type="button"
          onClick={onBrowseFieldCampaigns}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center">
            <MapPinned size={20} className="text-cyan-600 dark:text-cyan-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-gray-900 dark:text-white">Field campaigns</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fieldCampaignsSubtitle || 'Open your assigned campaigns on the map'}
            </p>
          </div>
          <ChevronRight size={20} className="text-gray-400 shrink-0" />
        </button>
      )}

      <button
        type="button"
        onClick={() => setCurrentSection('field_mapping')}
        className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
          <Route size={20} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">Field mapping</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Road snap distances ({settings.roadSnapAutoRadiusM} m auto / {settings.roadSnapSearchRadiusM} m search)
          </p>
        </div>
        <ChevronRight size={20} className="text-gray-400 shrink-0" />
      </button>

      {/* Map Layers - 3D Buildings & Advanced Settings */}
      <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
            <Box size={20} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white">Map Layers</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">3D buildings, terrain, overlays</p>
          </div>
        </div>
        <div className="space-y-2">
          {onToggle3D && (
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-xl">
              <div className="flex items-center gap-3">
                <Box size={18} className="text-gray-500 dark:text-gray-400" />
                <span className="text-gray-700 dark:text-gray-200">3D Buildings</span>
              </div>
              {renderToggle(is3DEnabled, (v) => onToggle3D(v))}
            </div>
          )}
          <button
            onClick={() => onOpenLayerSettings?.()}
            className="w-full flex items-center gap-3 p-3 bg-white dark:bg-gray-900 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <Settings2 size={18} className="text-gray-500 dark:text-gray-400" />
            <span className="flex-1 text-left text-gray-700 dark:text-gray-200">Advanced Layer Settings</span>
            <ChevronRight size={18} className="text-gray-400" />
          </button>
        </div>
      </div>

      <button
        onClick={() => setCurrentSection('navigation')}
        className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
          <Navigation size={20} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">Navigation</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Route preferences, avoid options</p>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </button>

      <button
        onClick={() => setCurrentSection('privacy')}
        className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
          <Shield size={20} className="text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">Privacy</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Location history, data sharing</p>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </button>

      <button
        onClick={() => setCurrentSection('security')}
        className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
          <ShieldCheck size={20} className="text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">Security</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Two-factor authentication</p>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </button>

      <button
        onClick={() => setCurrentSection('language')}
        className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
          <Languages size={20} className="text-teal-600 dark:text-teal-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-gray-900 dark:text-white">Language & Units</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">English, distance units</p>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </button>

      <InstallAppSection variant="card" className="mt-2" />

      <div className="border-t border-gray-100 dark:border-gray-800 mt-4 pt-4">
        <button
          onClick={() => setCurrentSection('help')}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <HelpCircle size={20} className="text-gray-500 dark:text-gray-400" />
          <span className="flex-1 text-left text-gray-700 dark:text-gray-200">Help & Support</span>
          <ChevronRight size={20} className="text-gray-400" />
        </button>

        <button
          onClick={() => setCurrentSection('feedback')}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <MessageSquare size={20} className="text-gray-500 dark:text-gray-400" />
          <span className="flex-1 text-left text-gray-700 dark:text-gray-200">Send Feedback</span>
          <ChevronRight size={20} className="text-gray-400" />
        </button>

        <button
          onClick={() => {
            useTourStore.getState().restartTour();
            onClose();
          }}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <RotateCcw size={20} className="text-gray-500 dark:text-gray-400" />
          <span className="flex-1 text-left text-gray-700 dark:text-gray-200">Restart Map Tour</span>
          <ChevronRight size={20} className="text-gray-400" />
        </button>

        <button
          onClick={() => {
            useTourStore.getState().restartCampaignTour();
            onClose();
          }}
          className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <RotateCcw size={20} className="text-gray-500 dark:text-gray-400" />
          <span className="flex-1 text-left text-gray-700 dark:text-gray-200">Restart Field Campaign Tour</span>
          <ChevronRight size={20} className="text-gray-400" />
        </button>
      </div>

      {/* Developer CTA */}
      <div className="mt-4 p-4 bg-gradient-to-br from-primary-50 to-cyan-50 dark:from-primary-900/20 dark:to-cyan-900/20 rounded-xl border border-primary-200/60 dark:border-primary-800/40">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
            <Code2 size={16} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Build with Coordal</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              Integrate verified Ghana addresses, geocoding, and maps into your apps.
            </p>
            <a
              href={CONSOLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-2 transition-colors"
            >
              Developer Console <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>

      <AppBrandFooter className="pt-3 pb-1" />
    </div>
  );

  const mapStyles: { id: MapStyle; accent: AppearanceAccentKey; Icon: LucideIcon; label: string }[] = [
    { id: 'standard', accent: 'emerald', Icon: MapIcon, label: 'Standard' },
    { id: 'dark', accent: 'indigo', Icon: Moon, label: 'Dark' },
    { id: 'satellite', accent: 'cyan', Icon: Satellite, label: 'Satellite' },
    { id: 'outdoors', accent: 'green', Icon: TreePine, label: 'Outdoors' },
    { id: 'navigation', accent: 'blue', Icon: Navigation2, label: 'Navigation' },
  ];

  const renderAppearance = () => (
    <div className="space-y-8">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">
          Map style
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
          {mapStyles.map((style) => {
            const isActive = currentMapStyle === style.id;
            const a = appearanceTileAccent[style.accent];
            const Icon = style.Icon;
            return (
              <button
                key={style.id}
                type="button"
                onClick={() => onMapStyleChange?.(style.id)}
                className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl transition-all ${isActive
                  ? a.card
                  : 'border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? a.iconWell : 'bg-gray-100 dark:bg-gray-800'
                    }`}
                >
                  <Icon size={18} strokeWidth={2} className={isActive ? a.icon : 'text-gray-400 dark:text-gray-500'} />
                </div>
                <span
                  className={`text-xs font-medium text-center leading-tight ${isActive ? a.label : 'text-gray-600 dark:text-gray-400'
                    }`}
                >
                  {style.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">
          App theme
        </p>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {(
            [
              { id: 'light' as const, accent: 'amber' as AppearanceAccentKey, Icon: Sun, label: 'Light' },
              { id: 'dark' as const, accent: 'slate' as AppearanceAccentKey, Icon: Moon, label: 'Dark' },
              { id: 'system' as const, accent: 'teal' as AppearanceAccentKey, Icon: Monitor, label: 'Auto' },
            ] as const
          ).map((t) => {
            const isActive = settings.theme === t.id;
            const a = appearanceTileAccent[t.accent];
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => updateSetting('theme', t.id)}
                className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl transition-all ${isActive
                  ? a.card
                  : 'border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? a.iconWell : 'bg-gray-100 dark:bg-gray-800'
                    }`}
                >
                  <Icon size={18} strokeWidth={2} className={isActive ? a.icon : 'text-gray-400 dark:text-gray-500'} />
                </div>
                <span
                  className={`text-xs font-medium ${isActive ? a.label : 'text-gray-600 dark:text-gray-400'}`}
                >
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const preferredModes: { value: Settings['preferredMode']; label: string; Icon: LucideIcon; accent: AppearanceAccentKey }[] = [
    { value: 'driving', label: 'Driving', Icon: Car, accent: 'blue' },
    { value: 'walking', label: 'Walking', Icon: PersonStanding, accent: 'emerald' },
    { value: 'cycling', label: 'Cycling', Icon: Bike, accent: 'cyan' },
    { value: 'transit', label: 'Transit', Icon: Bus, accent: 'indigo' },
  ];

  const snapRadiusOptions = (min: number, max: number, step: number) => {
    const out: number[] = [];
    for (let v = min; v <= max; v += step) out.push(v);
    return out;
  };

  const renderFieldMapping = () => (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Road endpoints can snap to nearby mapped road endpoints when you finish drawing or tracing.
        Enable snapping in the contribution panel.
      </p>
      <div>
        <label htmlFor="road-snap-auto" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Auto-snap distance
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-relaxed">
          Automatically snap endpoints within this distance when the connection is unambiguous.
        </p>
        <select
          id="road-snap-auto"
          value={settings.roadSnapAutoRadiusM}
          onChange={(e) => handleUpdateSetting('roadSnapAutoRadiusM', Number(e.target.value))}
          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-gray-900 dark:text-gray-100"
        >
          {snapRadiusOptions(SNAP_BAND_LIMITS.autoMin, SNAP_BAND_LIMITS.autoMax, 1).map((m) => (
            <option key={m} value={m}>{m} m</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="road-snap-search" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Search radius
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-relaxed">
          Maximum distance to search for nearby road endpoints. Candidates beyond the auto-snap distance may
          require manual confirmation. Must be at least the auto-snap distance.
        </p>
        <select
          id="road-snap-search"
          value={settings.roadSnapSearchRadiusM}
          onChange={(e) => handleUpdateSetting('roadSnapSearchRadiusM', Number(e.target.value))}
          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-gray-900 dark:text-gray-100"
        >
          {snapRadiusOptions(SNAP_BAND_LIMITS.searchMin, SNAP_BAND_LIMITS.searchMax, 1)
            .filter((m) => m >= settings.roadSnapAutoRadiusM)
            .map((m) => (
              <option key={m} value={m}>{m} m</option>
            ))}
        </select>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Preferred Mode</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {preferredModes.map((mode) => {
            const isActive = settings.preferredMode === mode.value;
            const a = appearanceTileAccent[mode.accent];
            const Icon = mode.Icon;
            return (
              <button
                key={mode.value}
                type="button"
                onClick={() => handleUpdateSetting('preferredMode', mode.value)}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${isActive
                  ? a.card
                  : 'border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? a.iconWell : 'bg-gray-100 dark:bg-gray-800'
                    }`}
                >
                  <Icon size={18} strokeWidth={2} className={isActive ? a.icon : 'text-gray-400 dark:text-gray-500'} />
                </div>
                <span className={`text-sm font-medium ${isActive ? a.label : 'text-gray-600 dark:text-gray-400'}`}>
                  {mode.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Route Options</h4>

        <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl opacity-70">
          <div>
            <p className="text-gray-700 dark:text-gray-200">Avoid Tolls</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Coming in next release</p>
          </div>
          <span className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 px-2.5 py-1 rounded-full bg-gray-200 dark:bg-gray-700">
            Soon
          </span>
        </div>

        <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl opacity-70">
          <div>
            <p className="text-gray-700 dark:text-gray-200">Avoid Highways</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Coming in next release</p>
          </div>
          <span className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 px-2.5 py-1 rounded-full bg-gray-200 dark:bg-gray-700">
            Soon
          </span>
        </div>
      </div>

      <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">Voice guidance and haptic feedback coming soon</p>
      </div>
    </div>
  );

  // Notifications section removed - no backend yet

  const renderPrivacy = () => (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <div className="flex-1">
          <p className="text-gray-700 dark:text-gray-200 font-medium">Location History</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Save places you visit so you can quickly return to them later. Your location history is stored locally on your device and helps personalise nearby suggestions.
          </p>
        </div>
        {renderToggle(settings.locationHistory, (v) => handleUpdateSetting('locationHistory', v))}
      </div>

      <div className="flex items-start justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <div className="flex-1">
          <p className="text-gray-700 dark:text-gray-200 font-medium">Sync search history to account</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            When on, recent searches are saved to your account and appear on other devices. When off, searches are kept
            on this device only.
          </p>
        </div>
        {renderToggle(settings.searchHistory, handleSearchHistoryToggle)}
      </div>

      <div className="flex items-start justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <div className="flex-1">
          <p className="text-gray-700 dark:text-gray-200 font-medium">Share Analytics</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Help improve Coordal Maps by sharing anonymous usage data. We use this to:
          </p>
          <ul className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 space-y-1">
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>Improve search accuracy and address matching across Ghana</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>Identify missing roads, buildings, and places on the map</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>Optimise route calculations and travel time estimates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>Fix incorrect or outdated location data</span>
            </li>
          </ul>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Your data is always anonymised and never shared with third parties.
          </p>
        </div>
        {renderToggle(settings.shareAnalytics, (v) => handleUpdateSetting('shareAnalytics', v))}
      </div>

      <div className="border-t border-gray-100 dark:border-gray-800 pt-4 mt-4">
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Data Management</h4>
        <div className="flex gap-2">
          <ClearButton label="Search History" onClear={clearSearchHistory} />
          <ClearButton label="Location History" onClear={clearLocationHistory} />
        </div>
      </div>
    </div>
  );

  const renderLanguage = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Language</h4>
        <div className="space-y-2">
          <div className="w-full p-3 rounded-xl border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-left">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium dark:text-gray-200">English</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Default language</p>
              </div>
              <Check size={18} className="text-primary-500" />
            </div>
          </div>
        </div>
        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">Ghanaian languages coming soon</p>
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">Twi, Frafra, Hausa, Ewe, Ga, and more</p>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Distance Unit</h4>
        <div className="grid grid-cols-2 gap-2">
          {([
            { value: 'km', label: 'Kilometers' },
            { value: 'mi', label: 'Miles' },
          ] as const).map((unit) => (
            <button
              key={unit.value}
              onClick={() => handleUpdateSetting('distanceUnit', unit.value)}
              className={`p-3 rounded-xl border-2 transition-colors ${settings.distanceUnit === unit.value
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
            >
              <span className="dark:text-gray-200">{unit.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderHelp = () => (
    <div className="space-y-4">
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <h4 className="font-medium text-gray-900 dark:text-white mb-1">Coordal Maps</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Ghana's most detailed map with verified addresses, postcodes, and real-time navigation.
        </p>
      </div>

      <div className="space-y-2">
        <a
          href={`${MARKETING_URL}/help`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm text-gray-700 dark:text-gray-200">Help Centre</span>
          <ExternalLink size={16} className="text-gray-400" />
        </a>
        <a
          href={`${MARKETING_URL}/docs`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm text-gray-700 dark:text-gray-200">API Documentation</span>
          <ExternalLink size={16} className="text-gray-400" />
        </a>
        <a
          href={`${MARKETING_URL}/community`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm text-gray-700 dark:text-gray-200">Community</span>
          <ExternalLink size={16} className="text-gray-400" />
        </a>
      </div>

      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Contact Us</h4>
        <a href="mailto:support@coordal.com" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
          support@coordal.com
        </a>
      </div>
    </div>
  );

  const renderFeedback = () => {
    return <FeedbackForm />;
  };

  const getSectionTitle = () => {
    switch (currentSection) {
      case 'appearance': return 'Appearance';
      case 'navigation': return 'Navigation';
      case 'field_mapping': return 'Field mapping';
      case 'privacy': return 'Privacy';
      case 'security': return 'Security';
      case 'language': return 'Language & Units';
      case 'help': return 'Help & Support';
      case 'feedback': return 'Send Feedback';
      default: return 'Settings';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[100] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 pb-4 flex items-center gap-3 z-10 pt-safe">
            {currentSection !== 'main' && (
              <button
                type="button"
                onClick={() => setCurrentSection('main')}
                className="p-2 -ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Back"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            <h2 className="text-lg font-semibold flex-1 dark:text-white">{getSectionTitle()}</h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {currentSection === 'main' && renderMainMenu()}
            {currentSection === 'appearance' && renderAppearance()}
            {currentSection === 'navigation' && renderNavigation()}
            {currentSection === 'field_mapping' && renderFieldMapping()}
            {currentSection === 'privacy' && renderPrivacy()}
            {currentSection === 'security' && <SecuritySection />}
            {currentSection === 'language' && renderLanguage()}
            {currentSection === 'help' && renderHelp()}
            {currentSection === 'feedback' && renderFeedback()}
          </div>
        </div>
      </motion.div>

      {searchHistoryOptOutOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-4 bg-slate-950/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="search-history-opt-out-title"
          onClick={() => setSearchHistoryOptOutOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/15">
                <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              </div>
              <div>
                <h3 id="search-history-opt-out-title" className="text-base font-semibold text-gray-900 dark:text-white">
                  Store searches on this device only?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  Your recent searches will stay on this browser, but they will{' '}
                  <strong className="font-semibold text-gray-800 dark:text-gray-100">not</strong> appear in Recent
                  Activity on your other devices.
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setSearchHistoryOptOutOpen(false)}
                className="rounded-xl border border-gray-200 dark:border-gray-600 px-4 py-2.5 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Keep syncing
              </button>
              <button
                type="button"
                onClick={confirmSearchHistoryDeviceOnly}
                className="rounded-xl bg-amber-600 hover:bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white"
              >
                This device only
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
